import { useEffect, useState } from "react";
import { CalendarDays, Plus, Paperclip, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage, openStoredFile, formatTanggalID } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ApprovalChain } from "@/components/ApprovalChain";

// 6 pilihan Jenis Pengajuan utama (Dinas Luar dikelola dari modul terpisah)
const JENIS_OPTIONS = [
  "Cuti",
  "Izin",
  "Sakit",
  "Tugas Luar",
  "Istirahat Haid",
  "Istirahat Melahirkan / Gugur Kandungan",
];

const IZIN_CLASSIFICATION_OPTIONS = ["Izin", "Izin Diluar Istirahat"];

const IZIN_SUBTYPE_OPTIONS = [
  "Menikah",
  "Menyunatkan Anak",
  "Membaptiskan Anak",
  "Menatahkan Gigi Anak",
  "Menikahkan Anak",
  "Keluarga Inti Meninggal Dunia",
  "Istri Melahirkan/Keguguran",
  "Keadaan Mendesak",
  "Bencana Alam",
];

function emptyForm() {
  return {
    jenis: "Cuti",
    izin_classification: "",
    izin_subtype: "",
    tanggal_mulai: "",
    tanggal_selesai: "",
    alasan: "",
    lampiran_base64: null,
    lampiran_filename: null,
  };
}

export default function CutiIzin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxMb, setMaxMb] = useState(2);
  const [saldo, setSaldo] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [mine, cfg, sal] = await Promise.all([
        api.get("/requests/mine"),
        api.get("/settings/pengajuan"),
        api.get("/cuti/saldo"),
      ]);
      setRows(mine.data);
      setMaxMb(cfg.data.max_lampiran_mb);
      setSaldo(sal.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) {
      setForm((s) => ({ ...s, lampiran_base64: null, lampiran_filename: null }));
      return;
    }
    if (f.size > maxMb * 1024 * 1024) {
      toast.error(`Ukuran lampiran maksimal ${maxMb}MB`);
      e.target.value = "";
      return;
    }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    setForm((s) => ({ ...s, lampiran_base64: b64, lampiran_filename: f.name }));
  }

  // Saat jenis berubah, reset field klasifikasi/subtype agar tidak menyimpan
  // data yang tidak relevan.
  function onJenisChange(v) {
    setForm((s) => ({
      ...s,
      jenis: v,
      izin_classification: "",
      izin_subtype: "",
    }));
  }

  // Saat klasifikasi diubah kembali menjadi "Izin", subtype dikosongkan.
  function onKlasifikasiChange(v) {
    setForm((s) => ({
      ...s,
      izin_classification: v,
      izin_subtype: v === "Izin Diluar Istirahat" ? s.izin_subtype : "",
    }));
  }

  async function submit() {
    if (!form.tanggal_mulai || !form.tanggal_selesai) {
      toast.error("Tanggal mulai dan selesai wajib diisi");
      return;
    }
    if (form.alasan.trim().length < 3) {
      toast.error("Alasan minimal 3 karakter");
      return;
    }
    if (form.jenis === "Izin") {
      if (!form.izin_classification) {
        toast.error("Klasifikasi Izin wajib dipilih");
        return;
      }
      if (form.izin_classification === "Izin Diluar Istirahat" && !form.izin_subtype) {
        toast.error("Jenis Izin Diluar Istirahat wajib dipilih");
        return;
      }
    }
    if (form.jenis === "Tugas Luar" && !form.lampiran_base64) {
      toast.error("Tugas Luar wajib melampirkan Surat Tugas atau screenshot instruksi atasan");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        jenis: form.jenis,
        tanggal_mulai: form.tanggal_mulai,
        tanggal_selesai: form.tanggal_selesai,
        alasan: form.alasan.trim(),
        lampiran_base64: form.lampiran_base64,
        lampiran_filename: form.lampiran_filename,
        izin_classification: form.jenis === "Izin" ? form.izin_classification : null,
        izin_subtype:
          form.jenis === "Izin" && form.izin_classification === "Izin Diluar Istirahat"
            ? form.izin_subtype
            : null,
      };
      const { data } = await api.post("/requests", payload);
      if (data.pesan) toast.info(data.pesan);
      else toast.success("Pengajuan berhasil dikirim");
      setOpen(false);
      setForm(emptyForm());
      await load();
      window.dispatchEvent(new Event("atlas:refresh-approvals"));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const sakitTanpaLampiran = form.jenis === "Sakit" && !form.lampiran_base64;
  const tugasLuarTanpaLampiran = form.jenis === "Tugas Luar" && !form.lampiran_base64;
  const showKlasifikasi = form.jenis === "Izin";
  const showSubtype = showKlasifikasi && form.izin_classification === "Izin Diluar Istirahat";
  const lampiranLabel =
    form.jenis === "Sakit"
      ? "Lampiran Medis (wajib)"
      : form.jenis === "Tugas Luar"
      ? "Surat Tugas / Bukti Instruksi Atasan (wajib)"
      : "Lampiran (opsional)";

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-cuti-izin">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Cuti &amp; Izin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Pengajuan Anda
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Ajukan Cuti, Izin, Sakit, atau Istirahat khusus — pantau status persetujuannya di sini.
          </p>
        </div>
        <Button
          onClick={() => { setForm(emptyForm()); setOpen(true); }}
          className="gap-2 bg-teal-700 hover:bg-teal-800"
          data-testid="request-add-btn"
        >
          <Plus size={16} /> Ajukan Baru
        </Button>
      </div>

      {/* Saldo Cuti Tahunan card */}
      {saldo ? (
        <div
          className="mt-6 rounded-xl border border-teal-200 bg-teal-50/40 p-5 flex flex-wrap items-center justify-between gap-4"
          data-testid="saldo-cuti-card"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-teal-700 mb-1">
              Saldo Cuti Tahun {saldo.year}
            </div>
            <div className="font-mono-clock text-3xl font-bold text-slate-900">
              <span data-testid="saldo-tersisa">{saldo.tersisa}</span>
              <span className="text-slate-400 text-lg"> / {saldo.kuota_tahunan} hari</span>
            </div>
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              <span>Terpakai: <b className="text-emerald-700">{saldo.terpakai} hari</b></span>
              <span>Menunggu: <b className="text-amber-700">{saldo.tertahan_menunggu} hari</b></span>
              {saldo.override_pegawai != null ? (
                <span className="text-teal-800 font-semibold">(override pribadi)</span>
              ) : null}
            </div>
            {saldo.awal_bergabung ? (
              <div className="text-[11px] text-slate-500 mt-1.5" data-testid="saldo-awal-bergabung">
                Tanggal Awal Bergabung: <b className="text-slate-700">{formatTanggalID(saldo.awal_bergabung)}</b>
              </div>
            ) : null}
          </div>
          <div className="w-full sm:w-64">
            <div className="h-2.5 rounded-full bg-teal-100 overflow-hidden flex">
              <div
                className="h-full bg-emerald-600 transition-all"
                style={{
                  width: `${Math.min(100, (saldo.terpakai / (saldo.kuota_tahunan || 1)) * 100)}%`,
                }}
              />
              <div
                className="h-full bg-amber-400 transition-all"
                style={{
                  width: `${Math.min(100, (saldo.tertahan_menunggu / (saldo.kuota_tahunan || 1)) * 100)}%`,
                }}
              />
            </div>
            <div className="text-[10px] text-slate-500 mt-1 flex gap-3">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-600" /> Disetujui</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400" /> Menunggu</span>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-6 space-y-4">
        {loading ? (
          <div className="text-center text-slate-500 py-14">Memuat…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white text-center py-14">
            <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <CalendarDays size={22} />
            </div>
            <div className="mt-3 font-semibold text-slate-700">Belum ada pengajuan</div>
            <div className="text-xs text-slate-500 mt-1">
              Klik "Ajukan Baru" untuk membuat pengajuan pertama Anda.
            </div>
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border border-slate-200 bg-white p-5"
              data-testid={`request-card-${r.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={r.jenis} />
                    <StatusBadge status={r.status} testId={`request-status-${r.id}`} />
                    {r.konversi_dari_sakit ? (
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                        Sakit → Izin (tanpa lampiran)
                      </span>
                    ) : null}
                    {r.jenis === "Izin" && r.izin_classification ? (
                      <span
                        className="text-[10px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"
                        data-testid={`request-klasifikasi-${r.id}`}
                      >
                        {r.izin_classification}
                        {r.izin_subtype ? ` • ${r.izin_subtype}` : ""}
                      </span>
                    ) : null}
                    {r.benefit_deduction_status ? (
                      <span
                        className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${
                          r.benefit_deduction_status === "Potong Tunjangan"
                            ? "text-rose-700 bg-rose-50 border-rose-200"
                            : "text-emerald-700 bg-emerald-50 border-emerald-200"
                        }`}
                        data-testid={`request-benefit-${r.id}`}
                      >
                        {r.benefit_deduction_status}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 font-semibold text-slate-900">
                    {formatTanggalID(r.tanggal_mulai)}
                    {r.tanggal_selesai !== r.tanggal_mulai ? ` — ${formatTanggalID(r.tanggal_selesai)}` : ""}
                    <span className="ml-2 text-xs font-normal text-slate-500">
                      ({r.jumlah_hari} hari)
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{r.alasan}</div>
                  {r.lampiran_path ? (
                    <button
                      onClick={() => openStoredFile(r.lampiran_path).catch(() => toast.error("Gagal membuka lampiran"))}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-800 hover:text-teal-900"
                      data-testid={`request-lampiran-${r.id}`}
                    >
                      <Paperclip size={12} /> {r.lampiran_nama || "Lihat lampiran"}
                    </button>
                  ) : null}
                </div>
                <div className="text-[11px] text-slate-400">
                  Diajukan {formatTanggalID(r.created_at?.slice(0, 10))}
                </div>
              </div>
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
                  Alur Persetujuan
                </div>
                <ApprovalChain chain={r.approval_chain} currentLevel={r.current_level} status={r.status} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Dialog pengajuan */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle>Ajukan Cuti / Izin</DialogTitle>
            <DialogDescription>
              Pengajuan akan diteruskan ke atasan Anda secara berjenjang.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Jenis Pengajuan</Label>
              <Select value={form.jenis} onValueChange={onJenisChange}>
                <SelectTrigger className="mt-1.5" data-testid="input-jenis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JENIS_OPTIONS.map((j) => (<SelectItem key={j} value={j}>{j}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            {showKlasifikasi ? (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                  Klasifikasi Izin
                </Label>
                <Select value={form.izin_classification} onValueChange={onKlasifikasiChange}>
                  <SelectTrigger className="mt-1.5" data-testid="input-izin-classification">
                    <SelectValue placeholder="Pilih klasifikasi…" />
                  </SelectTrigger>
                  <SelectContent>
                    {IZIN_CLASSIFICATION_OPTIONS.map((k) => (
                      <SelectItem key={k} value={k}>{k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {showSubtype ? (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                  Jenis Izin Diluar Istirahat
                </Label>
                <Select
                  value={form.izin_subtype}
                  onValueChange={(v) => setForm({ ...form, izin_subtype: v })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="input-izin-subtype">
                    <SelectValue placeholder="Pilih jenis izin…" />
                  </SelectTrigger>
                  <SelectContent>
                    {IZIN_SUBTYPE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Dari Tanggal</Label>
                <Input
                  type="date"
                  className="mt-1.5"
                  value={form.tanggal_mulai}
                  onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })}
                  data-testid="input-tanggal-mulai"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Sampai Tanggal</Label>
                <Input
                  type="date"
                  className="mt-1.5"
                  value={form.tanggal_selesai}
                  onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })}
                  data-testid="input-tanggal-selesai"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Alasan</Label>
              <Textarea
                className="mt-1.5"
                rows={3}
                placeholder="Tuliskan alasan pengajuan…"
                value={form.alasan}
                onChange={(e) => setForm({ ...form, alasan: e.target.value })}
                data-testid="input-alasan"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                {lampiranLabel} — maks {maxMb}MB
              </Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="mt-1.5"
                onChange={onFile}
                data-testid="input-lampiran"
              />
              {sakitTanpaLampiran ? (
                <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="sakit-warning">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  Tanpa lampiran medis, pengajuan Sakit akan otomatis tercatat sebagai <b>Izin</b>.
                </div>
              ) : null}
              {tugasLuarTanpaLampiran ? (
                <div className="mt-2 flex items-start gap-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-md p-2.5" data-testid="tugas-luar-warning">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  Pengajuan <b>Tugas Luar</b> wajib melampirkan Surat Tugas atau screenshot instruksi atasan. Setelah disetujui, absensi otomatis <b>Tugas Luar</b> tanpa perlu absen kantor.
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="request-cancel-btn">Batal</Button>
            <Button onClick={submit} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="request-submit-btn">
              {saving ? "Mengirim…" : "Kirim Pengajuan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
